<template>
  <section ref="sidebarContent" class="emh-side-filter">
    <component
      :is="showAccordion ? 'EmhSideFiltersAccordionContent' : 'EmhSideFiltersContent'"
      class="emh-side-filter__filters-content"
      :is-loading="isLoading"
      :filter-categories="sideFilterCategories"
      :selected-filters="selectedVisibleFilters"
      :selected-category-level="selectedCategoryLevel"
      data-test-id="emh-side-filter__filters-content"
      @select-subcategory="onSelectSubCategory"
      @change-filter="onChangeFilter"
      @change-category="onChangeCategory"
      @clear-all-filters="onClearAllFilters"
      @clear-category="onClearCategory"
    >
      <template #head>
        <emh-side-filters-content-head
          :is-loading="isLoading"
          :selected-category="selectedCategory"
          :selected-visible-filters="selectedVisibleFilters"
          :available-categories="availableCategories"
          :side-filter-categories="sideFilterCategories"
          @change-filter="onChangeFilter"
          @change-category="onChangeCategory"
          @clear-all-filters="onClearAllFilters"
        >
        </emh-side-filters-content-head>
      </template>
    </component>

    <button
      class="emh-side-filter__open-modal-button wb-button wb-button--primary wb-button--large"
      type="button"
      @click="onToggleFiltersModal"
    >
      <wb-icon class="wb-button__icon" :name.attr="'bds/filter/24'"></wb-icon>
      {{ $t('dcp.assortment.cars.filtersSearch.filters.showAllFilter') }}
    </button>

    <Teleport v-if="srpModalTarget" :to="srpModalTarget">
      <emh-side-filters-modal
        ref="modal"
        :is-loading="isLoading"
        :show="isFiltersModalShown"
        :selected-filters="selectedVisibleFilters"
        :selected-category-level="selectedCategoryLevel"
        @select-subcategory="onSelectSubCategory"
        @close="onToggleFiltersModal"
        @clear-all-filters="onClearAllFilters"
      >
        <template #content>
          <emh-side-filters-content
            :is-loading="isLoading"
            :filter-categories="sideFilterCategories"
            :selected-filters="selectedVisibleFilters"
            :selected-category-level="selectedCategoryLevel"
            @select-subcategory="onSelectSubCategory"
            @change-filter="onChangeFilter"
            @change-category="onChangeCategory"
            @clear-all-filters="onClearAllFilters"
            @clear-category="onClearCategory"
          >
            <template #head>
              <emh-side-filters-content-head
                :is-loading="isLoading"
                :selected-category="selectedCategory"
                :selected-visible-filters="selectedVisibleFilters"
                :available-categories="availableCategories"
                :side-filter-categories="sideFilterCategories"
                :show-reset-all="false"
                @change-filter="onChangeFilter"
                @change-category="onChangeCategory"
                @clear-all-filters="onClearAllFilters"
              >
              </emh-side-filters-content-head>
            </template>
          </emh-side-filters-content>
        </template>
      </emh-side-filters-modal>
    </Teleport>
  </section>
</template>

<script lang="ts">
import TestingGenericWrapper from '@dh-io-emh/shared/src/components/testingGenericWrapper/TestingGenericWrapper.vue';
import { TestOptionType, useABTesting } from '@dh-io-emh/shared/src/composables/ab-testing.composable';
import { useTracking } from '@dh-io-emh/shared/src/composables/tracking.composable';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import { receiver } from '@dh-io-emh/shared/src/utils/broadcaster.util';
import { getRootElement } from '@dh-io-emh/shared/src/utils/query-element.util';
import { onScroll } from '@dh-io-emh/shared/src/utils/scroll.util';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { computed, defineComponent, nextTick, onMounted, ref, toRefs } from 'vue';
import { useFilter } from '../../composables/filter.composable';
import { useSrpModalTargets } from '../../composables/srp-modals.composable';
import EmhSideFiltersAvailabilityCategory from '../sideFiltersCategories/sideFiltersAvailabilityCategory/sideFiltersAvailabilityCategory.vue';
import EmhSideFiltersCategoriesToggle from '../sideFiltersCategoriesToggle/SideFiltersCategoriesToggle.vue';
import EmhSideFiltersAccordionContent from '../sideFiltersContent/SideFiltersAccordionContent.vue';
import EmhSideFiltersContent from '../sideFiltersContent/SideFiltersContent.vue';
import EmhSideFiltersContentHead from '../sideFiltersContent/SideFiltersContentHead.vue';
import EmhSideFiltersModal from '../sideFiltersModal/SideFiltersModal.vue';

export default defineComponent({
  name: 'EmhSideFilters',
  components: {
    EmhSideFiltersContent,
    EmhSideFiltersModal,
    EmhSideFiltersAccordionContent,
    EmhSideFiltersAvailabilityCategory,
    EmhSideFiltersCategoriesToggle,
    EmhSideFiltersContentHead,
    TestingGenericWrapper,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'all-filters-pressed',
    'change-filter',
    'vehicle-assortment-changed',
    'clear-all-filters',
    'clear-category',
    'close-modal',
  ],

  setup(props, { emit }) {
    const productsStore = useProductsStore();
    const { selectedTab } = toRefs(productsStore.selectors);
    const { trackLinkClickOnce } = useTracking();
    const modal = ref(EmhSideFiltersModal);
    const isFiltersModalShown = ref(false);
    const sidebarContent = ref<HTMLElement | null>(null);
    const selectedCategoryLevel = ref<string>();

    const abTestingAccordion = useABTesting('SideFiltersAccordion');
    const showAccordion = computed(() => abTestingAccordion.target.value === TestOptionType.TARGET);

    const { srpModalTarget } = useSrpModalTargets();
    const {
      srp: { availableCategories },
    } = infuse('config');

    // WB-Modal scroll lock is not working properly (EMHRO-798)
    // as a temporary workaround another scroll lock solution is used
    const onToggleFiltersModal = async () => {
      let scrollContent = modal.value.$el?.querySelector?.('wb-modal-content');

      selectedCategoryLevel.value = '';
      isFiltersModalShown.value = !isFiltersModalShown.value;

      if (isFiltersModalShown.value) {
        await nextTick();
        scrollContent ||= modal.value.$el?.querySelector?.('wb-modal-content');
        disableBodyScroll(scrollContent);
      } else {
        enableBodyScroll(scrollContent);
      }

      emit('close-modal');
    };

    const { sideFilterCategories, selectedVisibleFilters, selectedCategory, categoryTabChanged, changeFilter } =
      useFilter({
        emit,
      });

    const onChangeFilter = (filter) => {
      changeFilter(filter);
    };

    const { trackLinkClick } = useTracking();

    const onClearAllFiltersTracking = () => {
      trackLinkClick({
        eventDetail: {
          category: 'filter',
          action: 'remove.filter_all_items',
          label: 'from_sideFilters',
        },
      });
      onClearAllFilters();
    };

    const onSelectSubCategory = (category?: string) => {
      if (!category) {
        trackLinkClickOnce('back.navigation', {
          eventDetail: {
            category: 'filter',
            action: `criteria.${selectedTab.value}`,
            label: 'back',
          },
        });
      }

      selectedCategoryLevel.value = category;
    };

    const onChangeCategory = (categoryName: string) => categoryTabChanged({ detail: categoryName });

    const onClearAllFilters = () => emit('clear-all-filters');

    const onClearCategory = (codes: string[]) => emit('clear-category', codes);

    const scrollHistory: { y: number; direction: 'up' | 'down' } = Object.seal({ y: 0, direction: 'down' });

    onScroll(() => {
      // check the direction of the scroll
      scrollHistory.direction = window.scrollY > scrollHistory.y ? 'down' : 'up';
      scrollHistory.y = window.scrollY;

      const filterContent = sidebarContent.value?.querySelector('.emh-filters-content') as HTMLElement;

      // scroll side-filters to top when scrolling up and the top of the filter content is reached
      if (scrollHistory.direction === 'up' && window.scrollY < filterContent.offsetTop) {
        sidebarContent.value?.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const resultsContent = getRootElement().querySelector('div') as HTMLElement;

        // scroll side-filters to bottom when scrolling down and the bottom of the filter content is reached
        if (scrollHistory.direction === 'down' && window.scrollY > resultsContent.scrollHeight - window.innerHeight) {
          sidebarContent.value?.scrollTo({ top: filterContent?.scrollHeight, behavior: 'smooth' });
        }
      }
    });

    onScroll(() => {
      calculateSideFilterOffset(
        window.scrollY < (sidebarContent.value?.querySelector('.emh-filters-content') as HTMLElement)?.offsetTop,
      );
    }, 15);

    onMounted(() => {
      setTimeout(() => {
        calculateSideFilterOffset(true);
      }, 1000);
    });

    const calculateSideFilterOffset = (start) => {
      const offset = start ? `${sidebarContent.value?.offsetTop}px` : '';

      document.documentElement.style.setProperty('--side-filters-offset', offset);
    };

    receiver('open-all-filters', onToggleFiltersModal);

    return {
      sidebarContent,
      isFiltersModalShown,
      selectedCategory,
      selectedCategoryLevel,
      sideFilterCategories,
      srpModalTarget,
      selectedVisibleFilters,
      onToggleFiltersModal,
      onSelectSubCategory,
      onChangeCategory,
      onClearAllFilters,
      onClearAllFiltersTracking,
      onClearCategory,
      onChangeFilter,
      showAccordion,
      modal,
      availableCategories,
    };
  },
});
</script>

<style lang="scss" scoped>
.emh-side-filter {
  position: sticky;
  top: var(--wb-spacing-m);
  max-height: calc(100vh - var(--side-filters-offset, var(--wb-spacing-m)));
  overflow-y: auto;
  resize: none;
  -webkit-overflow-scrolling: touch;

  &__filters-content {
    @include breakpoint-to(mq4) {
      display: none;
    }
  }

  &__open-modal-button {
    width: 100%;

    @include breakpoint-from(mq5) {
      display: none;
    }
  }
}
</style>
